import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { useLanguageStore } from '@/store/language'

// Public routes that need language prefix
const publicRoutes = [
  {
    path: '',
    name: 'Home',
    component: () => import('@/views/Home.vue'),
    meta: { title: 'Ana Sayfa' }
  },
  {
    path: 'about',
    name: 'About',
    component: () => import('@/views/About.vue'),
    meta: { title: 'Hakkımızda' }
  },
  {
    path: 'team',
    name: 'Team',
    component: () => import('@/views/Team.vue'),
    meta: { title: 'Ekibimiz' }
  },
  {
    path: 'contact',
    name: 'Contact',
    component: () => import('@/views/Contact.vue'),
    meta: { title: 'İletişim' }
  },
  {
    path: 'pricing',
    name: 'Pricing',
    component: () => import('@/views/Pricing.vue'),
    meta: { title: 'Fiyatlandırma' }
  },
  {
    path: 'why-choose-us',
    name: 'WhyChooseUs',
    component: () => import('@/views/WhyChooseUs.vue'),
    meta: { title: 'Neden Bizi Seçin' }
  },
  {
    path: 'career',
    name: 'Career',
    component: () => import('@/views/Career.vue'),
    meta: { title: 'Kariyer' }
  },
  // Dynamic detail route - catches parent/child slug patterns (e.g., /hizmetlerimiz/hizmet-1)
  {
    path: ':parentSlug/:slug',
    name: 'DynamicDetail',
    component: () => import('@/views/DynamicDetail.vue'),
    meta: { title: 'Detay' }
  },
  // Dynamic page route - catches custom slugs for static pages
  // This should be LAST in the publicRoutes array
  {
    path: ':pageSlug',
    name: 'DynamicPage',
    component: () => import('@/views/DynamicPage.vue'),
    meta: { title: 'Sayfa' }
  }
]

const routes = [
  // Root redirect to default language
  {
    path: '/',
    redirect: () => {
      const langStore = useLanguageStore()
      return `/${langStore.currentLanguageCode || 'tr'}`
    }
  },
  // Language-prefixed routes
  {
    path: '/:lang(tr|en)',
    component: () => import('@/components/RouterViewWrapper.vue'),
    children: publicRoutes
  },
  // Admin routes (no language prefix)
  {
    path: '/ntspra',
    component: () => import('@/admin/AdminLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'AdminDashboard',
        component: () => import('@/admin/AdminDashboard.vue'),
        meta: { title: 'Admin Dashboard' }
      },
      {
        path: 'pages',
        name: 'AdminPages',
        component: () => import('@/admin/AdminPages.vue'),
        meta: { title: 'Sayfa Yönetimi' }
      },
      {
        path: 'blog',
        name: 'AdminBlog',
        component: () => import('@/admin/AdminBlog.vue'),
        meta: { title: 'Blog Yönetimi' }
      },
      {
        path: 'services',
        name: 'AdminServices',
        component: () => import('@/admin/AdminServices.vue'),
        meta: { title: 'Hizmet Yönetimi' }
      },
      {
        path: 'projects',
        name: 'AdminProjects',
        component: () => import('@/admin/AdminProjects.vue'),
        meta: { title: 'Proje Yönetimi' }
      },
      {
        path: 'partners',
        name: 'AdminPartners',
        component: () => import('@/admin/AdminPartners.vue'),
        meta: { title: 'Partner Yönetimi' }
      },
      {
        path: 'references',
        name: 'AdminReferences',
        component: () => import('@/admin/AdminReferences.vue'),
        meta: { title: 'Referans Yönetimi' }
      },
      {
        path: 'team',
        name: 'AdminTeam',
        component: () => import('@/admin/AdminTeam.vue'),
        meta: { title: 'Takım Yönetimi' }
      },
      {
        path: 'testimonials',
        name: 'AdminTestimonials',
        component: () => import('@/admin/AdminTestimonials.vue'),
        meta: { title: 'Yorum Yönetimi' }
      },
      {
        path: 'settings',
        name: 'AdminSettings',
        component: () => import('@/admin/AdminSettings.vue'),
        meta: { title: 'Site Ayarları' }
      },
      {
        path: 'users',
        name: 'AdminUsers',
        component: () => import('@/admin/AdminUsers.vue'),
        meta: { title: 'Kullanıcı Yönetimi' }
      },
      {
        path: 'sliders',
        name: 'AdminSliders',
        component: () => import('@/admin/AdminSliders.vue'),
        meta: { title: 'Slider Yönetimi' }
      },
      {
        path: 'media',
        name: 'AdminMedia',
        component: () => import('@/admin/AdminMedia.vue'),
        meta: { title: 'Medya Yönetimi' }
      },
      {
        path: 'homepage',
        name: 'AdminHomepage',
        component: () => import('@/admin/AdminHomepage.vue'),
        meta: { title: 'Anasayfa Bölüm Yönetimi' }
      },
      {
        path: 'page-editor',
        name: 'AdminPageEditor',
        component: () => import('@/admin/AdminPageEditor.vue'),
        meta: { title: 'Sayfa Editörü' }
      },
      {
        path: 'menus',
        name: 'AdminMenus',
        component: () => import('@/admin/AdminMenus.vue'),
        meta: { title: 'Menü Yönetimi' }
      },
      {
        path: 'footer',
        name: 'AdminFooter',
        component: () => import('@/admin/AdminFooter.vue'),
        meta: { title: 'Footer Yönetimi' }
      },
      {
        path: 'mail-settings',
        name: 'AdminMailSettings',
        component: () => import('@/admin/AdminMailSettings.vue'),
        meta: { title: 'Mail Ayarları' }
      },
      {
        path: 'contact-messages',
        name: 'AdminContactMessages',
        component: () => import('@/admin/AdminContactMessages.vue'),
        meta: { title: 'İletişim Mesajları' }
      },
      {
        path: 'cookie-settings',
        name: 'AdminCookieSettings',
        component: () => import('@/admin/AdminCookieSettings.vue'),
        meta: { title: 'Çerez Ayarları' }
      },
      {
        path: 'legal-pages',
        name: 'AdminLegalPages',
        component: () => import('@/admin/AdminLegalPages.vue'),
        meta: { title: 'Yasal Sayfalar' }
      },
    ]
  },
  {
    path: '/ntspra/login',
    name: 'AdminLogin',
    component: () => import('@/admin/AdminLogin.vue'),
    meta: { title: 'Admin Girişi' }
  },
  // 404 Not Found - Language prefixed
  {
    path: '/:lang(tr|en)/404',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: { title: '404 - Sayfa Bulunamadı' }
  },
  // Catch all - redirect to 404
  {
    path: '/:pathMatch(.*)*',
    name: 'CatchAll',
    redirect: (to) => {
      // Try to extract language from path
      const pathParts = to.path.split('/').filter(Boolean)
      const lang = ['tr', 'en'].includes(pathParts[0]) ? pathParts[0] : 'tr'
      return `/${lang}/404`
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  const langStore = useLanguageStore()
  
  // Set language from route param
  if (to.params.lang) {
    langStore.setLanguage(to.params.lang)
  }
  
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'AdminLogin' })
  } else {
    next()
  }
})

export default router
